import { animaliaDetailDesignTokens } from '../shared/animalia-detail-design-tokens';

type DetailStatusVariant = 'loading' | 'not-found' | 'error';

export class MvAnimaliaDetailStatus extends HTMLElement {
  private variant: DetailStatusVariant = 'loading';

  private heading = '';

  private message = '';

  private showRetry = false;

  private backHref = '/';

  private readonly handleRetryClick = () => {
    this.dispatchEvent(
      new CustomEvent('retry-requested', {
        bubbles: true,
        composed: true,
      }),
    );
  };

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  setStatus(
    variant: DetailStatusVariant,
    title: string,
    body: string,
    showRetry = false,
    backHref = '/',
  ): void {
    this.variant = variant;
    this.heading = title;
    this.message = body;
    this.showRetry = showRetry;
    this.backHref = backHref;
    this.render();
  }

  connectedCallback(): void {
    this.render();
    this.addEventListeners();
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
  }

  private render(): void {
    if (!this.shadowRoot) {
      return;
    }

    this.shadowRoot.innerHTML = `
      <style>
        ${animaliaDetailDesignTokens}

        article {
          padding: 28px;
        }

        h2 {
          margin: 8px 0 0;
          color: #31463d;
          font-size: clamp(1.8rem, 2.6vw, 2.8rem);
          font-family: Georgia, 'Times New Roman', serif;
        }

        p:last-of-type {
          margin: 10px 0 0;
          line-height: 1.7;
          color: rgba(32, 39, 32, 0.64);
        }

        .actions {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 20px;
        }

        a,
        button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 12px 16px;
          border-radius: 999px;
          text-decoration: none;
          border: 1px solid #31463d;
          background: #31463d;
          color: #f7f3ed;
          cursor: pointer;
        }

        .secondary {
          background: transparent;
          color: #31463d;
        }
      </style>

      <article class="editorial-panel" data-variant="${this.variant}">
        <p class="status-kicker">Estado del detalle</p>
        <h2>${this.escapeHtml(this.heading)}</h2>
        <p>${this.escapeHtml(this.message)}</p>
        <div class="actions">
          ${this.showRetry ? '<button type="button" data-retry>Reintentar</button>' : ''}
          <a class="secondary" href="${this.escapeHtml(this.backHref)}">Volver a explorar</a>
        </div>
      </article>
    `;

    this.addEventListeners();
  }

  private addEventListeners(): void {
    this.shadowRoot?.querySelector('[data-retry]')?.addEventListener('click', this.handleRetryClick);
  }

  private removeEventListeners(): void {
    this.shadowRoot?.querySelector('[data-retry]')?.removeEventListener('click', this.handleRetryClick);
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

if (!customElements.get('mv-animalia-detail-status')) {
  customElements.define('mv-animalia-detail-status', MvAnimaliaDetailStatus);
}
